import { motion } from "framer-motion";
import { Zap, BadgeCheck, ShieldCheck, Send, Play, Music2, Type, Sparkles, MousePointer2 } from "lucide-react";
import { CAPCUT } from "../../config";

const buy = () => {
  window.location.href = CAPCUT.checkoutUrl;
};

const clips = [
  { label: "Clip 01", w: "22%", color: "bg-blue-600" },
  { label: "Clip 02", w: "18%", color: "bg-cyan-400" },
  { label: "Clip 03", w: "26%", color: "bg-cyan-500" },
  { label: "Clip 04", w: "16%", color: "bg-blue-500" },
  { label: "Clip 05", w: "14%", color: "bg-sky-400" },
];

const EditorMockup = () => (
  <motion.div
    initial={{ opacity: 0, y: 40, scale: 0.97 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    className="relative"
    data-testid="capcut-editor-mockup"
  >
    <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_30px_80px_rgb(15,23,42,0.12)] p-4 sm:p-5">
      <div className="flex items-center gap-1.5 mb-4">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-2 flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
          <Scissors2 /> CapCut Pro — Project.ccp
        </span>
        <span className="ml-auto rounded-full bg-blue-600 text-white text-[9px] font-bold px-2.5 py-1 uppercase tracking-wider">PRO</span>
      </div>

      <div className="relative rounded-xl bg-slate-900 aspect-video flex items-center justify-center overflow-hidden">
        <span className="absolute top-3 right-3 rounded-full bg-white/10 backdrop-blur border border-white/15 text-white text-[9px] font-bold px-2.5 py-1 flex items-center gap-1">
          <Sparkles className="w-2.5 h-2.5" /> 4K • Tanpa Watermark
        </span>
        <span className="w-14 h-14 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
          <Play className="w-6 h-6 text-white fill-white ml-0.5" />
        </span>
        <span className="absolute bottom-3 left-4 flex items-center gap-1.5 text-[10px] font-mono text-slate-300">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> 00:00:04:12 / 00:01:00:00
        </span>
        <MousePointer2 className="absolute bottom-1/3 right-1/4 w-4 h-4 text-white/80" />
      </div>

      <div className="flex justify-between text-[9px] font-mono text-slate-400 mt-3 px-0.5">
        {["00:00", "00:10", "00:20", "00:30", "00:40", "00:50"].map((t) => (
          <span key={t}>{t}</span>
        ))}
      </div>

      <div className="relative mt-1.5">
        <div className="flex gap-1 h-8 mb-1.5">
          {clips.map((c) => (
            <div key={c.label} className={`${c.color} rounded-md flex items-center px-2`} style={{ width: c.w }}>
              <span className="text-[8px] font-bold text-white whitespace-nowrap overflow-hidden">{c.label}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1.5 h-8 mb-1.5">
          <span className="w-6 h-full rounded-md bg-slate-100 flex items-center justify-center shrink-0">
            <Music2 className="w-3 h-3 text-slate-400" />
          </span>
          <div className="flex-1 h-full rounded-md bg-blue-50 border border-blue-100 flex items-center gap-[2px] px-2 overflow-hidden">
            {[...Array(56)].map((_, i) => (
              <span key={i} className="capcut-wave w-[2.5px] rounded-full bg-blue-500/80" style={{ animationDelay: `${(i % 10) * 0.13}s`, height: `${25 + ((i * 17) % 60)}%` }} />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1.5 h-7">
          <span className="w-6 h-full rounded-md bg-slate-100 flex items-center justify-center shrink-0">
            <Type className="w-3 h-3 text-slate-400" />
          </span>
          <span className="rounded-md border border-slate-200 bg-white px-3 h-full flex items-center text-[9px] font-semibold text-slate-500">Auto Captions</span>
          <span className="rounded-md bg-cyan-100 border border-cyan-200 px-3 h-full flex items-center text-[9px] font-semibold text-cyan-700">Transisi</span>
        </div>
        <div className="capcut-playhead absolute -top-1 bottom-0 w-[2px] bg-red-500 rounded-full">
          <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-red-500" />
        </div>
      </div>
    </div>

    <div className="absolute -top-5 -left-4 sm:-left-8 float-delayed rounded-xl bg-white shadow-[0_12px_36px_rgba(15,23,42,0.12)] border border-slate-200 px-3.5 py-2.5 flex items-center gap-2">
      <span className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white text-[9px] font-black">4K</span>
      <span className="text-xs font-bold text-slate-800">Export Ultra HD</span>
    </div>
    <div className="absolute -bottom-4 -right-2 sm:-right-6 float-slow2 rounded-xl bg-white shadow-[0_12px_36px_rgba(15,23,42,0.12)] border border-slate-200 px-3.5 py-2.5 flex items-center gap-2">
      <Sparkles className="w-4 h-4 text-blue-600" />
      <span className="text-xs font-bold text-slate-800">AI Tools & Auto Captions</span>
    </div>
  </motion.div>
);

const Scissors2 = () => (
  <svg className="w-3 h-3 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <circle cx="6" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <line x1="20" y1="4" x2="8.12" y2="15.88" />
    <line x1="14.47" y1="14.48" x2="20" y2="20" />
    <line x1="8.12" y1="8.12" x2="12" y2="12" />
  </svg>
);

export const CapCutHero = () => (
  <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden capcut-hero-bg">
    <div className="absolute inset-0 hero-grid pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
      <div>
        <motion.span
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700 mb-6"
          data-testid="capcut-hero-badge"
        >
          <Zap className="w-3.5 h-3.5" /> Aktifkan Sekarang
        </motion.span>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-capcut-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05]"
        >
          CapCut <span className="text-blue-600">Pro</span>
          <br />1 Bulan
          <br /><span className="text-blue-600">Full Akses.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.24, duration: 0.6 }}
          className="mt-4 font-capcut-heading text-3xl sm:text-4xl font-black text-cyan-500"
          data-testid="capcut-hero-price"
        >
          Hanya 59rb
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.34, duration: 0.6 }}
          className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed max-w-md"
        >
          Edit video jadi lebih mudah, cepat & profesional. Semua fitur Pro terbuka — tanpa watermark, ekspor 4K, template premium.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.46, duration: 0.6 }}
          className="mt-8"
        >
          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.96 }}
            onClick={buy}
            data-testid="capcut-hero-cta"
            className="rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-11 py-4 shadow-[0_4px_14px_0_rgb(37,99,235,0.39)] hover:shadow-[0_10px_30px_rgba(37,99,235,0.4)] transition-[box-shadow,background-color] duration-300"
          >
            BELI SEKARANG
          </motion.button>
          <div className="flex items-center gap-5 mt-6 text-sm font-semibold text-slate-600">
            <span className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-emerald-600" /> Resmi</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Aman</span>
            <span className="flex items-center gap-1.5"><Send className="w-4 h-4 text-blue-600" /> Dikirim via WhatsApp</span>
          </div>
        </motion.div>
      </div>

      <EditorMockup />
    </div>
  </section>
);
